import { useEffect, useState, type ReactNode } from "react";
import { Loader2, Shield } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Wordmark } from "@/components/brand/Wordmark";
import { WhistllLogo } from "@/components/brand/WhistllLogo";
import { supabase } from "@/lib/supabase";

interface VerifiedFactor {
  id: string;
  friendly_name: string | null;
}

/**
 * Wraps dashboard children. On mount it checks whether the current
 * session has reached the highest AAL the user is configured for -
 * effectively, "if the user has a verified TOTP factor but the session
 * is still aal1, force a challenge before letting them past".
 *
 * Why a wrapper rather than a route guard: it stays out of the way
 * for users with no TOTP factor (most users today), it can re-trigger
 * if the session refreshes mid-day, and it avoids rerouting URLs.
 */
export function MfaChallengeGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<
    | { kind: "checking" }
    | { kind: "ok" }
    | { kind: "challenge"; factor: VerifiedFactor }
  >({ kind: "checking" });
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  async function check() {
    setStatus({ kind: "checking" });
    const { data: aal, error: aalErr } =
      await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalErr) {
      // If the call fails, fail open - if the user can't even read
      // their AAL, blocking them on a half-loaded session feels worse
      // than letting them through. Sentry will pick up the error.
      console.error("[MfaChallengeGate] aal lookup failed", aalErr);
      setStatus({ kind: "ok" });
      return;
    }
    if (
      aal &&
      aal.currentLevel === "aal1" &&
      aal.nextLevel === "aal2"
    ) {
      // User has a verified factor but hasn't satisfied it on this
      // session yet. Look it up so we can challenge against it.
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verified = (factors?.totp ?? []).find(
        (f: { status: string }) => f.status === "verified",
      );
      if (verified) {
        setStatus({
          kind: "challenge",
          factor: { id: verified.id, friendly_name: verified.friendly_name },
        });
        return;
      }
    }
    setStatus({ kind: "ok" });
  }

  useEffect(() => {
    check();
    // Re-check on auth state changes (e.g. session refresh)
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      check();
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  async function verify() {
    if (status.kind !== "challenge") return;
    if (!/^[0-9]{6}$/.test(code)) {
      return toast.error("Enter your 6-digit authenticator code.");
    }
    setVerifying(true);
    const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({
      factorId: status.factor.id,
    });
    if (chalErr) {
      setVerifying(false);
      return toast.error(chalErr.message);
    }
    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId: status.factor.id,
      challengeId: chal.id,
      code,
    });
    setVerifying(false);
    if (verErr) {
      return toast.error(verErr.message);
    }
    setCode("");
    await check();
  }

  if (status.kind === "checking") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (status.kind === "ok") {
    return <>{children}</>;
  }

  // Challenge UI - full-page-ish blocker.
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="flex w-full max-w-md flex-col gap-5 rounded-2xl border border-border bg-card p-7 shadow-2xl">
        <div className="flex items-center gap-2.5">
          <WhistllLogo size={26} />
          <Wordmark size="text-2xl" />
        </div>
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-volt-subtle px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-volt">
            <Shield className="h-3 w-3" />
            Two-factor required
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Verify it's you
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Open your authenticator app and enter the 6-digit code for{" "}
            {status.factor.friendly_name ?? "your Whistll account"}.
          </p>
        </div>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Authenticator code</span>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            disabled={verifying}
            autoFocus
            className="font-mono tracking-[0.4em] text-center text-lg"
          />
        </label>

        <Button
          onClick={verify}
          disabled={verifying || code.length !== 6}
          size="lg"
        >
          {verifying ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying…
            </>
          ) : (
            <>Verify</>
          )}
        </Button>

        <p className="text-xs text-muted-foreground">
          Lost access to your authenticator? Email{" "}
          <a
            href="mailto:hello@whistll.com?subject=2FA%20recovery"
            className="font-semibold text-foreground underline underline-offset-2 hover:text-volt"
          >
            hello@whistll.com
          </a>
          .
        </p>

        <button
          type="button"
          onClick={async () => {
            await supabase.auth.signOut();
            window.location.href = "/login";
          }}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
